/**
 * AdminNotreSystemePage - Édition de la page "Notre Système"
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { 
  Save, 
  RefreshCw, 
  Image as ImageIcon, 
  Sparkles, 
  ExternalLink,
  Loader2,
  Type,
  FileText,
  BarChart3,
  Quote,
  Shield,
  Wand2,
  Settings2,
  RotateCcw,
  ArrowUp,
  ArrowDown,
  Minus,
  Mic
} from 'lucide-react';
import { cn } from '../lib/utils';
import { VoiceInput } from '../components/ui/VoiceInput';

// Positions verticales disponibles
const VERTICAL_POSITIONS = [
  { id: 'top', name: 'Haut', icon: ArrowUp, desc: 'Sujet en haut de l\'image' },
  { id: 'center', name: 'Centre', icon: Minus, desc: 'Sujet centré' },
  { id: 'bottom', name: 'Bas', icon: ArrowDown, desc: 'Sujet en bas de l\'image' },
];

// Styles visuels disponibles pour la génération IA
const VISUAL_STYLES = [
  { id: 'professional', name: 'Professionnel', desc: 'Photo professionnelle, éclairage studio' },
  { id: 'cinematic', name: 'Cinématique', desc: 'Style film, couleurs dramatiques' },
  { id: 'editorial', name: 'Éditorial', desc: 'Style magazine, haute qualité' },
  { id: 'minimalist', name: 'Minimaliste', desc: 'Épuré, focus sur le sujet' },
  { id: 'urban', name: 'Urbain', desc: 'Ambiance ville, streetwear' },
  { id: 'industrial', name: 'Industriel', desc: 'Entrepôts, logistique, usines' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour obtenir le token admin
const getToken = () => localStorage.getItem('admin_token');

// Composant pour afficher l'aperçu de l'image actuelle
function CurrentImagePreview({ sectionKey, sections }) {
  const [hasError, setHasError] = useState(false);
  
  let imgUrl = null;
  if (sectionKey === 'warehouse_image' || sectionKey === 'shipping_image') {
    imgUrl = sections[sectionKey];
  } else if (sections[sectionKey]) {
    imgUrl = sections[sectionKey].image;
  }
  
  if (!imgUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-500">
        Aucune image
      </div>
    );
  }
  
  if (hasError) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-500">
        Erreur de chargement
      </div>
    );
  }
  
  return (
    <img 
      src={imgUrl} 
      alt="Aperçu actuel" 
      className="w-full h-full object-cover"
      onError={() => setHasError(true)}
    />
  );
}

// Prompts par défaut pour chaque section (en français)
const DEFAULT_PROMPTS = {
  hero: 'Intérieur d\'entrepôt moderne avec équipe logistique, atmosphère professionnelle, éclairage lumineux, environnement e-commerce',
  warehouse_image: 'Grand entrepôt industriel avec étagères organisées, cartons, chariots élévateurs, travailleurs en action, photographie logistique professionnelle',
  quality_control: 'Équipe de contrôle qualité inspectant des produits en entrepôt, processus d\'inspection détaillé, photographie professionnelle, travailleurs avec documents',
  pricing: 'Entrepôt moderne et efficace avec station d\'emballage organisée, colis d\'expédition, opération logistique professionnelle',
  shipping_image: 'Port maritime international avec conteneurs cargo, grands navires, logistique mondiale, ciel dramatique, photographie professionnelle',
};

export function AdminNotreSystemePage() {
  const navigate = useNavigate();
  const { isLight } = useAdminTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [activeTab, setActiveTab] = useState('hero');
  
  // État pour la boîte de dialogue de génération IA
  const [imageGenDialog, setImageGenDialog] = useState({
    open: false,
    sectionKey: null,
    prompt: '',
    style: 'professional',
    sectionName: '',
    verticalPosition: 'center', // top, center, bottom
  });

  // Classes de style
  const cardClass = isLight ? 'bg-white border-gray-200' : 'bg-zinc-900/80 border-zinc-800';
  const textClass = isLight ? 'text-gray-900' : 'text-white';
  const mutedClass = isLight ? 'text-gray-600' : 'text-gray-400';
  const inputClass = isLight 
    ? 'bg-white border-gray-300 text-gray-900' 
    : 'bg-zinc-800 border-zinc-700 text-white';

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const res = await axios.get(`${API_URL}/api/marketing-pages/admin/notre-systeme`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPageData(res.data);
    } catch (err) {
      console.error('Error fetching page:', err);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (sectionKey, field, lang, value) => {
    setPageData(prev => {
      const newData = { ...prev };
      if (!newData.sections[sectionKey]) {
        newData.sections[sectionKey] = {};
      }
      if (field) {
        if (!newData.sections[sectionKey][field]) {
          newData.sections[sectionKey][field] = {};
        }
        newData.sections[sectionKey][field][lang] = value;
      } else {
        // Direct value (like images)
        newData.sections[sectionKey] = value;
      }
      return newData;
    });
  };

  const updateNestedField = (sectionKey, index, field, lang, value) => {
    setPageData(prev => {
      const newData = { ...prev };
      if (!newData.sections[sectionKey][index][field]) {
        newData.sections[sectionKey][index][field] = {};
      }
      newData.sections[sectionKey][index][field][lang] = value;
      return newData;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = getToken();
      await axios.put(
        `${API_URL}/api/marketing-pages/admin/notre-systeme`,
        { sections: pageData.sections },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Page enregistrée !');
    } catch (err) {
      console.error('Error saving:', err);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Ouvrir la boîte de dialogue de génération IA
  const openImageGenDialog = (sectionKey, sectionName) => {
    setImageGenDialog({
      open: true,
      sectionKey,
      prompt: DEFAULT_PROMPTS[sectionKey] || '',
      style: 'professional',
      sectionName,
      verticalPosition: 'center',
    });
  };

  // Réinitialiser le prompt au prompt par défaut
  const resetPromptToDefault = () => {
    const defaultPrompt = DEFAULT_PROMPTS[imageGenDialog.sectionKey] || '';
    setImageGenDialog(prev => ({ ...prev, prompt: defaultPrompt }));
    toast.success('Prompt réinitialisé');
  };

  // Fermer la boîte de dialogue
  const closeImageGenDialog = () => {
    setImageGenDialog(prev => ({ ...prev, open: false }));
  };

  // Générer l'image avec les paramètres personnalisés
  const generateImage = async () => {
    const { sectionKey, prompt, style, verticalPosition } = imageGenDialog;
    
    if (!prompt.trim()) {
      toast.error('Veuillez entrer une description');
      return;
    }

    try {
      setGeneratingImage(sectionKey);
      closeImageGenDialog();
      
      const token = getToken();
      const styleDesc = VISUAL_STYLES.find(s => s.id === style)?.name || 'professional';
      
      // Utiliser l'endpoint de génération d'images
      const res = await axios.post(
        `${API_URL}/api/ai/generate-image`,
        { 
          prompt: prompt,
          style: `${styleDesc} photography, high quality, modern, detailed`,
          vertical_position: verticalPosition
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.image_url) {
        // Mettre à jour l'image dans les données
        const imageUrl = res.data.image_url.startsWith('/api/') 
          ? `${API_URL}${res.data.image_url}` 
          : res.data.image_url;
        
        // Sauvegarder l'image
        await axios.put(
          `${API_URL}/api/marketing-pages/admin/notre-systeme/image`,
          { section_key: sectionKey, image_url: imageUrl },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Mettre à jour localement
        if (sectionKey === 'warehouse_image' || sectionKey === 'shipping_image') {
          updateField(sectionKey, null, null, imageUrl);
        } else {
          setPageData(prev => ({
            ...prev,
            sections: {
              ...prev.sections,
              [sectionKey]: {
                ...prev.sections[sectionKey],
                image: imageUrl
              }
            }
          }));
        }

        toast.success('Image générée et sauvegardée !');
      }
    } catch (err) {
      console.error('Error generating image:', err);
      toast.error('Erreur lors de la génération');
    } finally {
      setGeneratingImage(null);
    }
  };

  if (loading) {
    return (
      <AdminPageWrapper title="Page Notre Système" subtitle="Chargement...">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-mutagim-red" />
        </div>
      </AdminPageWrapper>
    );
  }

  if (!pageData) {
    return (
      <AdminPageWrapper title="Page Notre Système" subtitle="Page non trouvée">
        <div className="text-center py-20">
          <p className={mutedClass}>La page n'a pas été trouvée.</p>
        </div>
      </AdminPageWrapper>
    );
  }

  const sections = pageData.sections || {};

  return (
    <AdminPageWrapper 
      title="Page Notre Système" 
      subtitle="Modifiez le contenu de la page marketing"
    >
      <div className="space-y-6">
        {/* Actions */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => window.open('/notre-systeme', '_blank')}
            className={isLight ? 'border-gray-300' : 'border-zinc-700'}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Voir la page
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-mutagim-red hover:bg-red-700"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={cn(
            "grid grid-cols-5 w-full",
            isLight ? "bg-gray-100" : "bg-zinc-800"
          )}>
            <TabsTrigger value="hero" className="gap-2">
              <ImageIcon className="w-4 h-4" />
              Hero
            </TabsTrigger>
            <TabsTrigger value="content" className="gap-2">
              <FileText className="w-4 h-4" />
              Contenu
            </TabsTrigger>
            <TabsTrigger value="images" className="gap-2">
              <ImageIcon className="w-4 h-4" />
              Images
            </TabsTrigger>
            <TabsTrigger value="stats" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Stats
            </TabsTrigger>
            <TabsTrigger value="guarantees" className="gap-2">
              <Shield className="w-4 h-4" />
              Garanties
            </TabsTrigger>
          </TabsList>

          {/* Hero Tab */}
          <TabsContent value="hero" className="space-y-6 mt-6">
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className={textClass}>Section Hero</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Hero Title */}
                <div className="space-y-4">
                  <label className={cn("text-sm font-medium", textClass)}>Titre principal</label>
                  {['fr', 'en', 'he'].map(lang => (
                    <div key={lang} className="flex items-center gap-2">
                      <span className="w-8 text-xs text-gray-500 uppercase">{lang}</span>
                      <Input
                        value={sections.hero?.title?.[lang] || ''}
                        onChange={(e) => updateField('hero', 'title', lang, e.target.value)}
                        className={inputClass}
                        dir={lang === 'he' ? 'rtl' : 'ltr'}
                      />
                    </div>
                  ))}
                </div>

                {/* Hero Subtitle */}
                <div className="space-y-4">
                  <label className={cn("text-sm font-medium", textClass)}>Sous-titre</label>
                  {['fr', 'en', 'he'].map(lang => (
                    <div key={lang} className="flex items-center gap-2">
                      <span className="w-8 text-xs text-gray-500 uppercase">{lang}</span>
                      <Textarea
                        value={sections.hero?.subtitle?.[lang] || ''}
                        onChange={(e) => updateField('hero', 'subtitle', lang, e.target.value)}
                        className={inputClass}
                        rows={2}
                        dir={lang === 'he' ? 'rtl' : 'ltr'}
                      />
                    </div>
                  ))}
                </div>

                {/* Hero Image */}
                <div className="space-y-4">
                  <label className={cn("text-sm font-medium", textClass)}>Image de fond</label>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Input
                        value={sections.hero?.image || ''}
                        onChange={(e) => setPageData(prev => ({
                          ...prev,
                          sections: { ...prev.sections, hero: { ...prev.sections.hero, image: e.target.value } }
                        }))}
                        placeholder="URL de l'image"
                        className={inputClass}
                      />
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => openImageGenDialog('hero', 'Image Hero')}
                      disabled={generatingImage === 'hero'}
                      className={cn(
                        isLight ? 'border-gray-300' : 'border-zinc-700',
                        "gap-2"
                      )}
                    >
                      {generatingImage === 'hero' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Wand2 className="w-4 h-4" />
                      )}
                      Générer IA
                    </Button>
                  </div>
                  {sections.hero?.image && (
                    <img 
                      src={sections.hero.image} 
                      alt="Hero preview" 
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content" className="space-y-6 mt-6">
            {/* Intro */}
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className={textClass}>Introduction</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {['fr', 'en', 'he'].map(lang => (
                  <div key={lang} className="flex items-start gap-2">
                    <span className="w-8 text-xs text-gray-500 uppercase mt-2">{lang}</span>
                    <Textarea
                      value={sections.intro?.text?.[lang] || ''}
                      onChange={(e) => updateField('intro', 'text', lang, e.target.value)}
                      className={inputClass}
                      rows={3}
                      dir={lang === 'he' ? 'rtl' : 'ltr'}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Steps */}
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className={textClass}>Les 3 Étapes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {sections.steps?.map((step, index) => (
                  <div key={index} className="p-4 rounded-lg border border-zinc-700 space-y-4">
                    <h4 className={cn("font-medium", textClass)}>Étape {index + 1}</h4>
                    {['fr', 'en', 'he'].map(lang => (
                      <div key={lang} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="w-8 text-xs text-gray-500 uppercase">{lang}</span>
                          <Input
                            value={step.title?.[lang] || ''}
                            onChange={(e) => updateNestedField('steps', index, 'title', lang, e.target.value)}
                            placeholder="Titre"
                            className={cn(inputClass, "flex-1")}
                            dir={lang === 'he' ? 'rtl' : 'ltr'}
                          />
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="w-8"></span>
                          <Textarea
                            value={step.text?.[lang] || ''}
                            onChange={(e) => updateNestedField('steps', index, 'text', lang, e.target.value)}
                            placeholder="Description"
                            className={cn(inputClass, "flex-1")}
                            rows={2}
                            dir={lang === 'he' ? 'rtl' : 'ltr'}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Quote */}
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", textClass)}>
                  <Quote className="w-5 h-5" />
                  Citation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {['fr', 'en', 'he'].map(lang => (
                  <div key={lang} className="flex items-start gap-2">
                    <span className="w-8 text-xs text-gray-500 uppercase mt-2">{lang}</span>
                    <Textarea
                      value={sections.quote?.text?.[lang] || ''}
                      onChange={(e) => updateField('quote', 'text', lang, e.target.value)}
                      className={inputClass}
                      rows={2}
                      dir={lang === 'he' ? 'rtl' : 'ltr'}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Images Tab */}
          <TabsContent value="images" className="space-y-6 mt-6">
            {/* Warehouse Image */}
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className={textClass}>Image Entrepôt</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Input
                    value={sections.warehouse_image || ''}
                    onChange={(e) => updateField('warehouse_image', null, null, e.target.value)}
                    placeholder="URL de l'image"
                    className={cn(inputClass, "flex-1")}
                  />
                  <Button
                    variant="outline"
                    onClick={() => openImageGenDialog('warehouse_image', 'Image Entrepôt')}
                    disabled={generatingImage === 'warehouse_image'}
                    className={cn(
                      isLight ? 'border-gray-300' : 'border-zinc-700',
                      "gap-2"
                    )}
                  >
                    {generatingImage === 'warehouse_image' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Wand2 className="w-4 h-4" />
                    )}
                    Générer IA
                  </Button>
                </div>
                {sections.warehouse_image && (
                  <img src={sections.warehouse_image} alt="Warehouse" className="w-full h-48 object-cover rounded-lg" />
                )}
              </CardContent>
            </Card>

            {/* Quality Control Image */}
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className={textClass}>Image Contrôle Qualité</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Input
                    value={sections.quality_control?.image || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      sections: { ...prev.sections, quality_control: { ...prev.sections.quality_control, image: e.target.value } }
                    }))}
                    placeholder="URL de l'image"
                    className={cn(inputClass, "flex-1")}
                  />
                  <Button
                    variant="outline"
                    onClick={() => openImageGenDialog('quality_control', 'Image Contrôle Qualité')}
                    disabled={generatingImage === 'quality_control'}
                    className={cn(
                      isLight ? 'border-gray-300' : 'border-zinc-700',
                      "gap-2"
                    )}
                  >
                    {generatingImage === 'quality_control' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Wand2 className="w-4 h-4" />
                    )}
                    Générer IA
                  </Button>
                </div>
                {sections.quality_control?.image && (
                  <img src={sections.quality_control.image} alt="Quality Control" className="w-full h-48 object-cover rounded-lg" />
                )}
              </CardContent>
            </Card>

            {/* Shipping Image */}
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className={textClass}>Image Livraison/Port</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Input
                    value={sections.shipping_image || ''}
                    onChange={(e) => updateField('shipping_image', null, null, e.target.value)}
                    placeholder="URL de l'image"
                    className={cn(inputClass, "flex-1")}
                  />
                  <Button
                    variant="outline"
                    onClick={() => openImageGenDialog('shipping_image', 'Image Livraison/Port')}
                    disabled={generatingImage === 'shipping_image'}
                    className={cn(
                      isLight ? 'border-gray-300' : 'border-zinc-700',
                      "gap-2"
                    )}
                  >
                    {generatingImage === 'shipping_image' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Wand2 className="w-4 h-4" />
                    )}
                    Générer IA
                  </Button>
                </div>
                {sections.shipping_image && (
                  <img src={sections.shipping_image} alt="Shipping" className="w-full h-48 object-cover rounded-lg" />
                )}
              </CardContent>
            </Card>

            {/* Pricing Image */}
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className={textClass}>Image Prix Imbattables</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Input
                    value={sections.pricing?.image || ''}
                    onChange={(e) => setPageData(prev => ({
                      ...prev,
                      sections: { ...prev.sections, pricing: { ...prev.sections.pricing, image: e.target.value } }
                    }))}
                    placeholder="URL de l'image"
                    className={cn(inputClass, "flex-1")}
                  />
                  <Button
                    variant="outline"
                    onClick={() => openImageGenDialog('pricing', 'Image Prix Imbattables')}
                    disabled={generatingImage === 'pricing'}
                    className={cn(
                      isLight ? 'border-gray-300' : 'border-zinc-700',
                      "gap-2"
                    )}
                  >
                    {generatingImage === 'pricing' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Wand2 className="w-4 h-4" />
                    )}
                    Générer IA
                  </Button>
                </div>
                {sections.pricing?.image && (
                  <img src={sections.pricing.image} alt="Pricing" className="w-full h-48 object-cover rounded-lg" />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats" className="space-y-6 mt-6">
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className={textClass}>Statistiques</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {sections.stats?.map((stat, index) => (
                  <div key={index} className="p-4 rounded-lg border border-zinc-700 space-y-4">
                    <div className="flex items-center gap-4">
                      <Input
                        value={stat.value || ''}
                        onChange={(e) => {
                          const newStats = [...sections.stats];
                          newStats[index].value = e.target.value;
                          setPageData(prev => ({
                            ...prev,
                            sections: { ...prev.sections, stats: newStats }
                          }));
                        }}
                        placeholder="Valeur (ex: 50 000+)"
                        className={cn(inputClass, "w-32")}
                      />
                      <span className={mutedClass}>—</span>
                      {['fr', 'en', 'he'].map(lang => (
                        <Input
                          key={lang}
                          value={stat.label?.[lang] || ''}
                          onChange={(e) => updateNestedField('stats', index, 'label', lang, e.target.value)}
                          placeholder={`Label (${lang})`}
                          className={cn(inputClass, "flex-1")}
                          dir={lang === 'he' ? 'rtl' : 'ltr'}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Guarantees Tab */}
          <TabsContent value="guarantees" className="space-y-6 mt-6">
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className={textClass}>Garanties</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {sections.guarantees?.map((guarantee, index) => (
                  <div key={index} className="p-4 rounded-lg border border-zinc-700 space-y-4">
                    <h4 className={cn("font-medium", textClass)}>Garantie {index + 1}</h4>
                    {['fr', 'en', 'he'].map(lang => (
                      <div key={lang} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="w-8 text-xs text-gray-500 uppercase">{lang}</span>
                          <Input
                            value={guarantee.title?.[lang] || ''}
                            onChange={(e) => updateNestedField('guarantees', index, 'title', lang, e.target.value)}
                            placeholder="Titre"
                            className={cn(inputClass, "flex-1")}
                            dir={lang === 'he' ? 'rtl' : 'ltr'}
                          />
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="w-8"></span>
                          <Textarea
                            value={guarantee.text?.[lang] || ''}
                            onChange={(e) => updateNestedField('guarantees', index, 'text', lang, e.target.value)}
                            placeholder="Description"
                            className={cn(inputClass, "flex-1")}
                            rows={2}
                            dir={lang === 'he' ? 'rtl' : 'ltr'}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog de génération d'image IA */}
      <Dialog open={imageGenDialog.open} onOpenChange={(open) => !open && closeImageGenDialog()}>
        <DialogContent className={cn(
          "max-w-2xl",
          isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"
        )}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", textClass)}>
              <Wand2 className="w-5 h-5 text-mutagim-red" />
              Générer une image IA - {imageGenDialog.sectionName}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Description / Prompt */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className={textClass}>Description de l'image souhaitée</Label>
                <div className="flex items-center gap-2">
                  <VoiceInput 
                    onTranscript={(text) => setImageGenDialog(prev => ({ ...prev, prompt: prev.prompt + ' ' + text }))}
                    className={isLight ? "border-gray-300" : "border-zinc-700"}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetPromptToDefault}
                    className={cn("h-7 text-xs gap-1", mutedClass, "hover:text-mutagim-red")}
                  >
                    <RotateCcw className="w-3 h-3" />
                    Prompt par défaut
                  </Button>
                </div>
              </div>
              <Textarea
                value={imageGenDialog.prompt}
                onChange={(e) => setImageGenDialog(prev => ({ ...prev, prompt: e.target.value }))}
                placeholder="Décrivez l'image que vous souhaitez générer... ou utilisez le micro"
                className={cn(inputClass, "min-h-[100px]")}
                rows={4}
              />
              <p className={cn("text-xs", mutedClass)}>
                Plus votre description est détaillée, meilleur sera le résultat.
              </p>
            </div>

            {/* Position verticale */}
            <div className="space-y-3">
              <Label className={textClass}>Position verticale du sujet</Label>
              <div className="flex gap-2">
                {VERTICAL_POSITIONS.map(pos => {
                  const Icon = pos.icon;
                  return (
                    <button
                      key={pos.id}
                      type="button"
                      onClick={() => setImageGenDialog(prev => ({ ...prev, verticalPosition: pos.id }))}
                      className={cn(
                        "flex-1 p-3 rounded-lg border text-center transition-all flex flex-col items-center gap-1",
                        imageGenDialog.verticalPosition === pos.id
                          ? "border-mutagim-red bg-mutagim-red/10"
                          : isLight 
                            ? "border-gray-200 hover:border-gray-300 bg-gray-50" 
                            : "border-zinc-700 hover:border-zinc-600 bg-zinc-800/50"
                      )}
                    >
                      <Icon className={cn("w-4 h-4", imageGenDialog.verticalPosition === pos.id ? "text-mutagim-red" : mutedClass)} />
                      <div className={cn("font-medium text-sm", textClass)}>{pos.name}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Style visuel */}
            <div className="space-y-3">
              <Label className={textClass}>Style visuel</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {VISUAL_STYLES.map(style => (
                  <button
                    key={style.id}
                    type="button"
                    onClick={() => setImageGenDialog(prev => ({ ...prev, style: style.id }))}
                    className={cn(
                      "p-3 rounded-lg border text-left transition-all",
                      imageGenDialog.style === style.id
                        ? "border-mutagim-red bg-mutagim-red/10"
                        : isLight 
                          ? "border-gray-200 hover:border-gray-300 bg-gray-50" 
                          : "border-zinc-700 hover:border-zinc-600 bg-zinc-800/50"
                    )}
                  >
                    <div className={cn("font-medium text-sm", textClass)}>{style.name}</div>
                    <div className={cn("text-xs mt-1", mutedClass)}>{style.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Aperçu de l'image actuelle */}
            {imageGenDialog.sectionKey && pageData?.sections && (
              <div className="space-y-2">
                <Label className={mutedClass}>Image actuelle</Label>
                <div className="h-32 rounded-lg overflow-hidden bg-zinc-800/50 relative">
                  <CurrentImagePreview 
                    sectionKey={imageGenDialog.sectionKey} 
                    sections={pageData.sections} 
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={closeImageGenDialog}
              className={isLight ? "" : "border-zinc-700"}
            >
              Annuler
            </Button>
            <Button 
              onClick={generateImage}
              disabled={!imageGenDialog.prompt.trim() || generatingImage}
              className="bg-mutagim-red hover:bg-red-700"
            >
              {generatingImage ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Génération...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Générer l'image
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageWrapper>
  );
}

export default AdminNotreSystemePage;
